import type { Phrase } from '../types';

/**
 * Strings for the home area of the public site. Every key starts with `hm`.
 * In titles, `*words*` marks the part lit in gold (the design's .accent).
 */
export const homeEn = {
  // opening intro
  hmIntroTag: 'Auditoriums · Wedding halls · Event venues',
  hmIntroSkip: 'Skip',

  // hero
  hmHeroLine1: 'Find the space.',
  hmHeroLine2: 'Make the *moment.*',
  hmSlideWeddings: 'Weddings',
  hmSlideBanquet: 'Banquet halls',
  hmSlideConcerts: 'Concerts',
  hmSlideConferences: 'Conferences',
  hmSlideReceptions: 'Receptions',
  hmSlideShow: 'Show photo {n} of {total}: {caption}',
  hmSlidePause: 'Pause background slideshow',
  hmSlidePlay: 'Play background slideshow',
  hmPause: 'Pause',
  hmPlay: 'Play',

  // search
  hmSearchLabel: 'Search venues',
  hmSearchLocation: 'Location',
  hmSearchAnywhere: 'Anywhere',
  hmSearchEvent: 'Event type',
  hmSearchAnyEvent: 'Any event',
  hmSearchDate: 'Date',
  hmSearchDatePh: 'Any date',
  hmSearchGuests: 'Guests',
  hmSearchGuestsPh: 'How many?',
  hmSearchGo: 'Search venues',

  // why (the Aghosha promise)
  hmWhyLabel: 'The Aghosha promise',
  hmWhyNote: 'Verified · Real dates · No online payment',
  hmTrustTitle: 'Why plan with *Aghosha*',
  hmTrust1Title: 'Checked before listing',
  hmTrust1Body: "An Aghosha admin reviews every venue's licences and owner ID before it goes live.",
  hmTrust2Title: 'Real availability',
  hmTrust2Body: 'See which dates and sessions are free before you send a request.',
  hmTrust3Title: 'Compare side by side',
  hmTrust3Body: 'Put two venues side by side and compare capacity, facilities and prices row by row.',
  hmTrust4Title: 'No online payment',
  hmTrust4Body: 'The venue confirms your request and you pay them directly. Updates reach you by notification and SMS.',

  // featured venues
  hmFeatEyebrow: 'Featured venues',
  hmFeatNote: 'Photos, capacity and prices up front',
  hmFeatTitle: 'Venues worth a *closer look*',
  hmFeatSub: 'Verified venues taking requests now. Open one to see its free dates and full price list.',
  hmFeatAll: 'See all venues',
  hmFeatList: 'Featured venues',
  hmFeatView: 'View venue',
  hmFeatCapacity: 'Capacity',
  hmFeatFrom: 'From',
  hmGuests: { one: '{count} guest', other: '{count} guests' },

  // occasions
  hmOccEyebrow: 'Occasions',
  hmOccNote: 'From engagements to conferences',
  hmOccTitle: 'Find a venue for *every occasion*',
  hmOccSub: 'Pick your event to see the venues that suit it and what to check before you book.',
  hmOccAll: 'All event types',
  hmOccLabel: 'Occasion',
  hmVenueCount: { one: '{count} venue', other: '{count} venues' },

  // statement
  hmPromoEyebrow: 'Made for moments that matter',
  hmPromoTitle: 'Your event deserves the *right venue*',
  hmPromoBody:
    'Grand ballrooms, elegant wedding halls and full-scale auditoriums, each listed with its seating, prices and free dates. Compare them in one place, then plan the day with the venue itself.',
  hmPromoCta: 'Explore all venues',

  // how it works
  hmHowEyebrow: 'How it works',
  hmHowNote: 'Nothing is charged online',
  hmHowTitle: 'From first search to *confirmed date*',
  hmHowSub: 'A booking on Aghosha is a request, not a payment. The venue confirms it, and you pay the venue directly.',
  hmHowGuide: 'Read the full guide',
  hmStepN: 'Step {n}',
  hmStep1Title: 'Discover',
  hmStep1Body: 'Search by location, guest count and the kind of event you are planning.',
  hmStep2Title: 'Compare',
  hmStep2Body: 'Look through photos, facilities, prices and open dates side by side.',
  hmStep3Title: 'Request',
  hmStep3Body: 'Pick a date and session and send a booking request. Nothing is charged online.',
  hmStep4Title: 'Confirm',
  hmStep4Body: 'The venue confirms your request. You pay them directly at the venue and get every update by notification.',

  // spotlight
  hmSpotEyebrow: 'Venue spotlight',
  hmSpotTitle: 'One venue, *up close*',
  hmSpotReviews: { one: '{count} review', other: '{count} reviews' },
  hmSpotNew: 'New on Aghosha',
  hmSpotCapacity: 'Capacity',
  hmSpotGuests: { one: '{count} guest', other: '{count} guests' },
  hmSpotFrom: 'Starting from',
  hmSpotOnRequest: 'Price on request',
  hmSpotFacilities: 'What it offers',
  hmSpotAc: 'Air-conditioned',
  hmSpotLift: 'Lift access',
  hmSpotCarParking: { one: 'Parking for {count} car', other: 'Parking for {count} cars' },
  hmSpotParking: 'Parking',
  hmSpotCatering: 'Catering: {value}',
  hmSpotCta: 'Explore venue',
  hmSpotPhoto: 'Show photo {n} of {name}',

  // locations
  hmLocEyebrow: 'Locations',
  hmLocNote: 'More cities joining',
  hmLocTitle: 'Venues *by city*',
  hmLocSub: 'Every city with listed venues, and how many you can request there.',
  hmLocAll: 'All locations',
  hmLocBrowseTitle: 'Not sure where yet?',
  hmLocBrowseBody: 'Browse every listed venue and narrow it down by date, guests and event.',
  hmLocBrowseCta: 'Browse all venues',

  // event types
  hmEvEyebrow: 'Event types',
  hmEvNote: { one: '{count} kind of event', other: '{count} kinds of events' },
  hmEvTitle: 'Spaces for every kind of *celebration*',
  hmEvSub: 'Choose the kind of event and see the venues that usually suit it.',

  // guides
  hmInspEyebrow: 'Inspiration',
  hmInspNote: 'Practical planning guides',
  hmInspTitle: 'Plan it well, *from the first idea*',
  hmInspSub: 'Practical guides on choosing, setting up and booking a venue.',
  hmInspAll: 'All guides',
  hmInspRead: 'Read more',
  hmInspMins: '{count} min read',
  hmCatPlanning: 'Planning',
  hmCatWeddings: 'Weddings',
  hmCatCorporate: 'Corporate',
  hmCatVenues: 'Choosing a venue',

  // ideas (photo wall)

  // reviews
  hmRevEyebrow: 'Guest reviews',
  hmRevNote: 'From guests who booked',
  hmRevTitle: 'Loved by event organisers',
  hmRevSub: 'Only guests who booked a venue through Aghosha can review it.',
  hmRevRated: 'Rated {rating} out of 5',
  hmRevBooked: 'booked',
  hmRevShow: 'Show review {n} of {total}',

  // numbers
  hmFigEyebrow: 'Aghosha in numbers',
  hmFigNote: 'Growing as venues join',
  hmFigTitle: 'A growing network *of venues*',
  hmFigVenues: { one: 'Venue listed', other: 'Venues listed' },
  hmFigVenuesNote: 'Verified before going live',
  hmFigCities: { one: 'City covered', other: 'Cities covered' },
  hmFigCitiesNote: 'More cities joining',
  hmFigEvents: { one: 'Event confirmed', other: 'Events confirmed' },
  hmFigEventsNote: 'Requests confirmed by venues',
  hmFigRating: 'Average guest rating',
  hmFigRatingNote: { one: 'from {count} review', other: 'from {count} reviews' },

  // ring call to action
  hmRingText: 'FIND THE SPACE  ✦  MAKE THE MOMENT  ✦  ',
  hmFinalEyebrow: 'Start planning',
  hmFinalTitle: 'Ready to find your *venue?*',
  hmFinalBody: 'Search by city, date and guest count, then send a request to the venue. Nothing is paid online.',
  hmFinalExplore: 'Explore venues',
  hmFinalList: 'List your venue',
} satisfies Record<string, Phrase>;
